import React, { useContext, useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import SearchHashtags from "../../components/search/SearchHashtags";
import { Filter } from "./Filter";
import FeedAdvertisement from "../../components/feeds/sidebar/FeedAdvertisement";
import { useLocation } from "react-router-dom";
import { useInfiniteQuery } from "react-query";
import { getAllSearchAgainstKeywordApi } from "../../services/searchApis";
import InfiniteScroll from "react-infinite-scroller";
import { toast } from "react-toastify";
import CourseCard from "../../components/courses/cards/CourseCard";
import { Language, LoginUserData } from "../../App";
import { useSplashAds } from "../../hooks/useSplashAds";
import SearchResultCard from "../../components/search/SearchResultCard";
import { LuGraduationCap, LuBookOpen, LuSearch } from "react-icons/lu";

export default function CoursesSearch() {
  const query = useLocation().search;
  const { lang } = useContext(Language);
  const keyword = decodeURIComponent(query.split("=")[1] || "");
  const { splashData } = useContext(LoginUserData);
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [date, setDate] = useState(null);
  const [cost, setCost] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  const specialization = selectedSpecializations;
  const sort = sortBy;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["search-courses", keyword, cost, selectedSpecializations, sortBy],
      queryFn: ({ pageParam = 1 }) =>
        getAllSearchAgainstKeywordApi({
          keyword,
          type: "courses",
          pageParam,
          specialization,
          date,
          cost: cost?.toLowerCase(),
          sort,
        }),
      enabled: !!keyword,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const courses = data?.pages?.flatMap((page) => page?.data?.data) || [];
  const leftAds = splashAds?.search_left || [];
  const rightAds = splashAds?.search_right || [];
  const hashTags = data?.pages?.flatMap((page) => page?.hash_tags);
  const storedValue = JSON.parse(localStorage.getItem("contest_obj"));

  useEffect(() => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  }, [selectedSpecializations, date, cost, sort, refetch]);

  return (
    <div className="search-page-wrapper">
      <NavBar />
      <Filter
        title={lang?.courses || "Courses"}
        options={[
          {
            title: lang?.specialization || "Specialization",
            options: splashData?.home?.subcategories,
          },
          {
            title: lang?.sort_by || "Sort By",
            options: [lang?.newest_to_oldest || "Newest to Oldest", lang?.oldest_to_newest || "Oldest to Newest"],
          },
          { title: lang?.cost || "Cost", options: [lang?.free || "Free", lang?.paid || "Paid"] },
          { title: lang?.date || "Date", options: [] },
        ]}
        onSpecializationSelect={(specializations) =>
          setSelectedSpecializations(specializations ? specializations.map((spec) => spec.value) : [])
        }
        onSelectDate={(date) => setDate(date)}
        onSortBy={(sort) => setSortBy(sort)}
        onSelectCost={(cost) => setCost(cost)}
      />

      <div className="search-layout-container">
        {/* Left Column: Ads */}
        <div className="search-left-col">
          <FeedAdvertisement
            ads={leftAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>

        {/* Center Column: Results */}
        <div className="search-main-col">
          <div className="search-results-header-inline">
            <h1 className="search-main-heading">
              <span>{lang?.courses || "Courses"}</span>
              {keyword && (
                <span className="search-keyword-badge">
                  <LuSearch size={14} />
                  <span>"{keyword}"</span>
                </span>
              )}
            </h1>
            {!isLoading && !isRefetching && courses?.length > 0 && (
              <span className="search-results-summary">
                {courses.length} {courses.length === 1 ? (lang?.result || "result") : (lang?.results || "results")} {lang?.found || "found"}
              </span>
            )}
          </div>

          <CoursesList
            loading={isLoading || isRefetching}
            data={courses}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        {/* Right Column: Hashtags & Ads */}
        <div className="search-right-col">
          <SearchHashtags data={hashTags} />
          <FeedAdvertisement
            ads={rightAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>
      </div>
    </div>
  );
}

const CoursesList = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  loading,
}) => {
  const { lang } = useContext(Language);

  return (
    <SearchResultCard
      icon={<LuGraduationCap />}
      title={lang?.courses || "Courses"}
      count={data?.length}
      seeAll={false}
      showHeader={false}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (!isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={hasNextPage}
        loader={
          <div className="search-courses-list mt-3">
            {[1].map((n) => (
              <div
                key={n}
                className="search-section-card p-3 d-flex flex-column flex-sm-row gap-3"
              >
                <div
                  className="search-shimmer-box rounded-3"
                  style={{ width: "100%", maxWidth: 160, height: 100, minWidth: 120 }}
                />
                <div className="flex-grow-1 d-flex flex-column justify-content-center">
                  <div className="search-shimmer-box mb-2" style={{ height: 16, width: "70%" }} />
                  <div className="search-shimmer-box mb-2" style={{ height: 13, width: "45%" }} />
                  <div className="search-shimmer-box" style={{ height: 12, width: "30%" }} />
                </div>
              </div>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="search-courses-list">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className="search-section-card p-3 d-flex flex-column flex-sm-row gap-3"
              >
                <div
                  className="search-shimmer-box rounded-3"
                  style={{ width: "100%", maxWidth: 160, height: 100, minWidth: 120 }}
                />
                <div className="flex-grow-1 d-flex flex-column justify-content-center">
                  <div className="search-shimmer-box mb-2" style={{ height: 16, width: "70%" }} />
                  <div className="search-shimmer-box mb-2" style={{ height: 13, width: "45%" }} />
                  <div className="search-shimmer-box" style={{ height: 12, width: "30%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : data && data.length > 0 ? (
          <div className="search-courses-list">
            {data.map((item, index) => (
              <CourseCard key={item?.id || index} course={item} />
            ))}
          </div>
        ) : (
          <div className="search-empty-state-card">
            <div className="search-empty-icon-wrap">
              <LuBookOpen />
            </div>
            <h5 className="search-empty-title">{lang?.no_courses_found || "No courses found"}</h5>
            <p className="search-empty-desc">
              {lang?.try_different_keyword || "Try a different keyword or check spelling."}
            </p>
          </div>
        )}
      </InfiniteScroll>
    </SearchResultCard>
  );
};
